<template lang="pug">
  .order-overview.v-col-12
    .overview-select-wrapper
      .overview
        .order-number {{ __('ORDER:') }} #[strong {{ order.orderNumber }}]
        p(
          v-if="!currentPackage.isPickup && currentPackage.processedDate"
        ) {{ __('Order sent in') }} &nbsp;
          span.font-weight-bold.
            {{ `${packagesQty > 0 ? packagesQty : ''} ${packagesQty > 1  ? __('Shipments') : __('Shipment')}` }}
        p(v-if="currentPackage.isPickup") {{ __('Pickup Order') }}
      package-select(:packages="orderPackages")
    progress-bar(:current-package="currentPackage")
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'
import { type LocationQueryValue, useRoute } from 'vue-router'
import type { IRssPackage, TrackedOrder } from '../rssTypes'
import packageSelect from './package-select.vue'
import progressBar   from './progress-bar.vue'

const props = withDefaults(defineProps<{
  order?: Pick<TrackedOrder, 'orderNumber' | 'packages'>
}>(), {
  order: () => ({
    orderNumber : '',
    packages    : [],
  }),
})

const route = useRoute()

const currentPackageIndex = ref<number>(0)

// Get the amount of packages the order was sent
const packagesQty = computed<number>(() => orderPackages.value?.length || 0)

// Get the current package based on `packageId` in the url
const currentPackage = computed<IRssPackage>(() => props.order.packages![currentPackageIndex.value] || {})

const orderPackages = computed(() => {
  return props.order?.packages && props.order.packages.length > 1
    ? props.order.packages.filter((pkg) => pkg.items && pkg.items.length > 0)
    : props.order?.packages
})

function setCurrentPackageIndex(packageId: LocationQueryValue | LocationQueryValue[]): void {
  const idAsNum = Number(packageId)
  currentPackageIndex.value = (!isNaN(idAsNum) && idAsNum > 0)
    ? (idAsNum - 1)
    : 0
}

watch(() => route.query.packageId, (packageId) => {
  setCurrentPackageIndex(packageId)
})

onBeforeMount(() => {
  const { query: { packageId } } = route
  setCurrentPackageIndex(packageId)
})
</script>

<style lang="scss" scoped>
  .order-overview {
    position      : relative;
    text-align    : center;
    background    : $color-gray-x-light;
    padding       : $space-xxs 0;
    margin-bottom : $space-s;
    box-shadow    : $shadow-s;

    .overview-select-wrapper {
      display         : flex;
      justify-content : space-between;
      padding         : 0 $space-xxs;

      .overview {
        margin-bottom : $space-s;
        margin-right  : $space-xs;
        white-space   : nowrap;
        text-align    : left;

        .order-number {
          font-family : $font-condensed;
          font-size   : $size-l;
          line-height : $line-height-m;
        }

        p {
          font-size     : $size-xs;
          margin-bottom : 0;
        }
      }
    }

    @media(min-width : $desktop) {
      display         : flex;
      justify-content : space-between;
      align-items     : center;
      text-align      : left;
      padding         : $space-xs 0;
      margin          : 0 0 $space-s;
      box-shadow      : none;

      .overview-select-wrapper {
        justify-content : flex-start;
        padding         : 0 $space-xs 0 $space-l;

        .overview {
          margin-bottom : 0;
          margin-right  : $space-l;
        }
      }
    }
  }
</style>
